/*
  Aligns the pushes of a collection product grid on the surrounding product cards:
  a push spanning N rows must end exactly on the bottom edge of the Nth row of
  card images. CSS cannot express this — the target depends on the height of the
  card info block, which varies with the product title wrapping — hence the
  measurement below.

  Recomputed on: first paint, web fonts ready, viewport width change, column
  count change, grid re-render (facets) and section reload (theme editor).
*/
(function () {
  var GRID_SELECTOR = '.product-grid.product-grid--has-push';
  // Cover pushes fill their whole cell through CSS alone and need no measuring.
  var PUSH_SELECTOR = '.grid__item--push:not(.grid__item--push-cover)';
  var MOBILE_BREAKPOINT = 990; // keep in sync with component-push.css

  var scheduled = false;
  var lastWidth = 0;

  // Element carrying the height: the editorial push sizes its own <li>, the
  // product push sizes the image box so the <li> keeps room for the card info.
  function sizedElement(push) {
    if (push.classList.contains('grid__item--push-editorial')) return push;
    return push.querySelector('.card__inner');
  }

  function alignGrid(grid) {
    // --push-span-rows is read here, before any write below, so the whole loop
    // stays read-then-write and does not force a style recalc per push.
    var pushes = [].map.call(grid.querySelectorAll(PUSH_SELECTOR), function (push) {
      return {
        element: sizedElement(push),
        rows: parseInt(getComputedStyle(push).getPropertyValue('--push-span-rows'), 10) || 2
      };
    }).filter(function (entry) {
      return entry.element;
    });
    if (!pushes.length) return;

    // Clearing first is what makes the measurement trustworthy: a height computed
    // for another column count stretches the rows the push spans, which would
    // inflate the card height read below as the reference.
    pushes.forEach(function (entry) {
      entry.element.style.height = '';
    });

    if (window.innerWidth < MOBILE_BREAKPOINT) return;

    var card = grid.querySelector('.grid__item:not(.grid__item--push)');
    if (!card) return;
    var image = card.querySelector('.card__inner.ratio')
      || card.querySelector('.card__inner')
      || card.querySelector('.card__media');
    if (!image) return;

    // Reading geometry flushes the reset above.
    var imageHeight = image.getBoundingClientRect().height;
    if (!imageHeight) return;
    var infoHeight = Math.max(0, card.getBoundingClientRect().height - imageHeight);
    var rowGap = parseFloat(getComputedStyle(grid).rowGap) || 0;

    pushes.forEach(function (entry) {
      // Top of the row 1 image -> bottom of the row N image.
      var height = entry.rows * imageHeight + (entry.rows - 1) * (infoHeight + rowGap);
      if (height > 0) entry.element.style.height = height + 'px';
    });
  }

  function align() {
    scheduled = false;
    [].forEach.call(document.querySelectorAll(GRID_SELECTOR), alignGrid);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(align);
  }

  // The column selector rewrites grid--N-col-desktop on the grid in place, so its
  // class list is the only signal of the new layout. align() writes inline styles
  // on descendants only, so this cannot loop.
  var columnsObserver = new MutationObserver(schedule);

  function refresh() {
    [].forEach.call(document.querySelectorAll(GRID_SELECTOR), function (grid) {
      columnsObserver.observe(grid, { attributes: true, attributeFilter: ['class'] });
    });
    schedule();
  }

  function init() {
    lastWidth = window.innerWidth;
    refresh();
    // The info height depends on text metrics, so it is not final until the web
    // fonts have swapped in. The rAF hop is required: fonts.ready is usually
    // already resolved here, and its microtask would run before the frame
    // refresh() just queued, where the `scheduled` guard would swallow it.
    if (document.fonts) {
      document.fonts.ready.then(function () {
        requestAnimationFrame(schedule);
      });
    }
    // Last resort net for the deferred scripts that fill the card info after
    // load (quick-add, price-per-item, rating widgets): they change the info
    // height, which no load or font event announces.
    setTimeout(schedule, 600);
  }

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }

  window.addEventListener('resize', function () {
    if (window.innerWidth === lastWidth) return; // ignore mobile URL bar height changes
    lastWidth = window.innerWidth;
    schedule();
  });

  // facets.js replaces #ProductGridContainer.innerHTML, discarding the grid node
  // the observer was attached to, then fires this event. collection-grid-layout.js
  // listens to it too, to re-apply the stored column count; deferring to a
  // microtask lets it run first whatever the listener registration order, so we
  // never measure against the server-rendered column count.
  document.addEventListener('grid-layout:refresh', function () {
    Promise.resolve().then(refresh);
  });
  document.addEventListener('shopify:section:load', refresh);
})();
